import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalServiceImpl } from '../service/modal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, merge, Subject, tap, withLatestFrom } from 'rxjs';
import { isDefined } from '@pool-league/shared-util-misc';

@Component({
  selector: 'lib-modal-host',
  imports: [CommonModule],
  templateUrl: './modal-host.component.html',
  styleUrl: './modal-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalHostComponent {
  readonly #modalClosed$ = new Subject<void>();
  readonly #clearLastCallback$ = new Subject<undefined>();

  readonly #modalService = inject(ModalServiceImpl);

  readonly #renderModal$ = this.#modalService.renderModal$.pipe(
    map(({ component, context, callback }) => ({
      component,
      inputs: {
        context,
        resolver: {
          resolve: (returnValue: unknown) => {
            this.#clearLastCallback$.next(undefined);
            this.dialog?.nativeElement.close();
            callback(returnValue);
          },
        },
      },
    })),
    tap(() => this.dialog?.nativeElement.showModal()),
  );

  readonly #dismissModal$ = this.#modalService.dismissModal$.pipe(
    tap(() => this.dialog?.nativeElement.close()),
  );

  readonly modalContent$ = merge(this.#renderModal$, this.#dismissModal$);

  @ViewChild('dialogElement', { read: ElementRef })
  dialog?: ElementRef<HTMLDialogElement>;

  constructor() {
    const currentCallback$ = merge(
      this.#clearLastCallback$,
      this.#modalService.renderModal$.pipe(map((modal) => modal.callback)),
    );
    this.#modalClosed$
      .pipe(
        takeUntilDestroyed(),
        withLatestFrom(currentCallback$),
        map(([, callback]) => callback),
        filter(isDefined),
      )
      .subscribe((callback) => {
        this.#clearLastCallback$.next(undefined);
        callback(undefined);
      });
  }

  modalClosed() {
    this.#modalClosed$.next();
  }
}
